/**
 * Brass Birmingham — Profile + Friends + Recent Games API client
 *
 * All calls go through the API gateway via HttpOnly BFF cookies.
 * The `${API_GATEWAY_URL}/brass` prefix maps to brass-cluster on the gateway.
 * Use authFetch() from auth.js for CSRF-protected mutating requests.
 */

import { pickErrorCode, throwApiError } from '../../shared/js/api-error.js';
import { authFetch } from '../../shared/js/auth.js';

const BRASS_BASE = `${window.API_GATEWAY_URL ?? ''}/brass`;

/**
 * GET /api/users/me/profile
 * Auto-creates profile on first call. Returns { userId, displayName, rating, gamesPlayed, gamesWon, createdAt, updatedAt }.
 * @returns {Promise<object>}
 */
export async function getProfile() {
  const url = `${BRASS_BASE}/api/users/me/profile`;
  const response = await authFetch(url);
  if (!response.ok) await throwApiError(response, url);
  return response.json();
}

/**
 * PATCH /api/users/me/profile — update display name.
 * Returns { ok: true } on success (200), { ok: false, code: 'ERR_NAME_TAKEN' } on conflict (409).
 * @param {string} displayName
 * @returns {Promise<{ ok: boolean, code?: string }>}
 */
export async function updateDisplayName(displayName) {
  const url = `${BRASS_BASE}/api/users/me/profile`;
  const response = await authFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName })
  });
  if (response.status === 409) {
    return { ok: false, code: await conflictCode(response, 'ERR_NAME_TAKEN') };
  }
  if (!response.ok) await throwApiError(response, url);
  return { ok: true };
}

/**
 * PATCH /api/users/me/profile — update display name and/or preferred colour together.
 * The backend requires a valid displayName on every update, so callers pass the current
 * (or edited) name alongside the colour. `preferredColor` may be one of the four player
 * colours or null to clear it.
 * Returns { ok: true } on success, or { ok: false, code } for ERR_NAME_TAKEN (409) /
 * ERR_INVALID_COLOR (400).
 * @param {{ displayName: string, preferredColor?: string|null }} update
 * @returns {Promise<{ ok: boolean, code?: string }>}
 */
export async function updateProfile({ displayName, preferredColor = null }) {
  const url = `${BRASS_BASE}/api/users/me/profile`;
  const response = await authFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName, preferredColor })
  });
  if (response.status === 409 || response.status === 400) {
    const fallback = response.status === 409 ? 'ERR_NAME_TAKEN' : 'ERR_INVALID_COLOR';
    return { ok: false, code: await conflictCode(response, fallback) };
  }
  if (!response.ok) await throwApiError(response, url);
  return { ok: true };
}

/**
 * GET /api/users/me/friends?status={status}
 * Returns PagedResult<FriendResponse>. Caller reads .items.
 * @param {'accepted'|'pending'|'all'} status
 * @returns {Promise<object>}
 */
export async function getFriends(status = 'accepted') {
  const url = `${BRASS_BASE}/api/users/me/friends?status=${encodeURIComponent(status)}`;
  const response = await authFetch(url);
  if (!response.ok) await throwApiError(response, url);
  return response.json();
}

/**
 * GET /api/users/search?q={prefix}
 * Returns up to 20 profiles matching the prefix (excludes requester).
 * @param {string} q
 * @returns {Promise<Array<{ userId: string, displayName: string }>>}
 */
export async function searchUsers(q) {
  const url = `${BRASS_BASE}/api/users/search?q=${encodeURIComponent(q)}`;
  const response = await authFetch(url);
  if (!response.ok) await throwApiError(response, url);
  return response.json();
}

/**
 * POST /api/users/me/friends/request — send a friend request.
 * Idempotent: re-requesting an existing row is a no-op (200).
 * @param {string} targetUserId
 * @returns {Promise<void>}
 */
export async function requestFriend(targetUserId) {
  const url = `${BRASS_BASE}/api/users/me/friends/request`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUserId })
  });
  if (!response.ok) await throwApiError(response, url);
}

/**
 * POST /api/users/me/friends/{friendUserId}/accept
 * Only the non-initiator can accept; returns 403 if initiator calls this.
 * @param {string} friendUserId
 * @returns {Promise<void>}
 */
export async function acceptFriend(friendUserId) {
  const url = `${BRASS_BASE}/api/users/me/friends/${encodeURIComponent(friendUserId)}/accept`;
  const response = await authFetch(url, { method: 'POST' });
  if (!response.ok) await throwApiError(response, url);
}

/**
 * DELETE /api/users/me/friends/{friendUserId}
 * Dispatches: reject pending (non-initiator), cancel pending (initiator), or unfriend (accepted).
 * @param {string} friendUserId
 * @returns {Promise<void>}
 */
export async function deleteFriend(friendUserId) {
  const url = `${BRASS_BASE}/api/users/me/friends/${encodeURIComponent(friendUserId)}`;
  const response = await authFetch(url, { method: 'DELETE' });
  if (!response.ok) await throwApiError(response, url);
}

/**
 * GET /api/users/me/games/recent?limit=5
 * Returns array of recent game summaries: { gameId, status, opponents, updatedAt }.
 * @returns {Promise<Array<object>>}
 */
export async function getRecentGames() {
  const url = `${BRASS_BASE}/api/users/me/games/recent?limit=5`;
  const response = await authFetch(url);
  if (!response.ok) await throwApiError(response, url);
  return response.json();
}

/**
 * GET /api/users/me/games — paginated game history with optional status filter and sort.
 * Returns PagedResult<GameHistoryRowDto>.
 * @param {{ status?: string, page?: number, pageSize?: number, sortBy?: string, sortDesc?: boolean }} options
 * @returns {Promise<object>}
 */
export async function getGameHistory({ status = 'all', page = 1, pageSize = 50, sortBy, sortDesc } = {}) {
  const params = new URLSearchParams({ status, page: String(page), pageSize: String(pageSize) });
  if (sortBy) params.set('sortBy', sortBy);
  if (sortDesc !== undefined) params.set('sortDesc', String(sortDesc));
  const url = `${BRASS_BASE}/api/users/me/games?${params.toString()}`;
  const response = await authFetch(url);
  if (!response.ok) await throwApiError(response, url);
  return response.json();
}

/**
 * Resolve the machine code for an expected profile conflict (409/400) that the
 * caller surfaces as `{ ok: false, code }` rather than throwing. Routes the
 * response through the shared error parser and reads `.code` off the thrown
 * error — no bespoke ProblemDetails parsing here — falling back to the
 * status-derived default when the body names no code.
 * @param {Response} response
 * @param {string} url
 * @param {string} fallback  status-derived default code
 * @returns {Promise<string>}
 */
async function conflictCode(response, fallback) {
  // Expected validation outcome (name taken / invalid colour) — read the code
  // through the shared parser without throwApiError's Sentry side-effect.
  const body = await response.json().catch(() => null);
  return pickErrorCode(body) ?? fallback;
}
